import { useState } from "react";
import { toast } from "sonner";
import { Wand2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const Counts = ({ c }) => (Object.keys(c || {}).length
  ? <div className="flex flex-wrap gap-1" data-testid="protect-counts">{Object.entries(c).map(([k, v]) => <span key={k} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-800">{k}: {v}</span>)}</div>
  : <span className="text-[11px] text-slate-400">No PII detected</span>);

export function RedactionTool({ meta }) {
  const [mode, setMode] = useState("policy");
  const [purpose, setPurpose] = useState("Export");
  const [text, setText] = useState("");
  const [out, setOut] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = () => (mode === "policy" || mode === "redact" ? { mode, method: "" } : { mode: "policy", method: mode });
  const runText = async () => {
    try { const { data } = await api.post("/protection/text", { text, purpose, ...body(), method: body().method || null }); setOut(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const runFile = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("purpose", purpose);
    Object.entries(body()).forEach(([k, v]) => fd.append(k, v));
    try {
      const r = await api.post("/protection/file", fd, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "protected";
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      toast.success(`Protected copy downloaded (${r.headers["x-pii-counts"] || "none"})`);
    } catch (e) { toast.error(e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="redaction-tool">
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1"><Label className="text-xs">Protection</Label>
          <Select value={mode} onValueChange={setMode}><SelectTrigger className="w-64" data-testid="protect-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="policy">Apply matching policy</SelectItem><SelectItem value="redact">Redact everything (irreversible)</SelectItem>
              {meta.methods.filter((m) => m.key !== "redact").map((m) => <SelectItem key={m.key} value={m.key}>Force: {m.label}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Purpose</Label>
          <Select value={purpose} onValueChange={setPurpose}><SelectTrigger className="w-52" data-testid="protect-purpose"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.purposes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2"><Label className="text-xs">Text</Label>
          <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste text containing names, emails, phone, PAN, Aadhaar, UPI…" data-testid="protect-input" />
          <Button onClick={runText} disabled={!text.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="protect-text-button"><Wand2 className="mr-1.5 h-4 w-4" />Protect text</Button></div>
        <div className="space-y-2"><Label className="text-xs">Result (not stored)</Label>
          <Textarea rows={8} readOnly value={out?.text || ""} className="bg-slate-50 font-mono text-xs" data-testid="protect-output" />{out && <Counts c={out.counts} />}</div>
      </div>
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div className="space-y-1"><Label className="text-xs">Document (PDF, DOCX, XLSX, CSV, TXT)</Label><Input type="file" accept=".pdf,.docx,.xlsx,.csv,.txt,.md" onChange={(e) => setFile(e.target.files[0])} data-testid="protect-file-input" /></div>
        <Button onClick={runFile} disabled={!file || busy} variant="outline" data-testid="protect-file-button"><FileDown className="mr-1.5 h-4 w-4" />{busy ? "Processing…" : "Download protected copy"}</Button>
        <p className="w-full text-[11px] text-slate-500">Redaction removes the underlying text — PDFs are rebuilt from text with the original text layer, images and metadata removed (layout simplified). DOCX/XLSX values are replaced in the file itself and author metadata is cleared.</p>
      </div>
    </div>
  );
}
